import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Camera, Flashlight, RotateCcw, SwitchCamera, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import QrScanner from "qr-scanner";
import { supabase } from "@/integrations/supabase/client";
import { resolveQrForCompany, logQrScanActivity } from "@/lib/qr-utils";

const QRScanner = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [cameras, setCameras] = useState<QrScanner.Camera[]>([]);
  const [currentCameraId, setCurrentCameraId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Load user profile
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setError('Kullanıcı oturumu bulunamadı');
        navigate('/login');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        setError('Kullanıcı profili yüklenemedi');
        return;
      }

      setUserProfile(profile);
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError('Kullanıcı profili yüklenirken hata oluştu');
    } finally {
      setLoadingProfile(false);
    }
  };

  // Initialize camera and QR scanner
  useEffect(() => {
    let mounted = true;

    const initializeScanner = async () => {
      try {
        // Check if camera is available
        const hasCamera = await QrScanner.hasCamera();
        if (!hasCamera) {
          setError("Kamera bulunamadı");
          return;
        }

        // Get available cameras
        const availableCameras = await QrScanner.listCameras(true);
        if (mounted) {
          setCameras(availableCameras);
          // Prefer back camera for mobile devices
          const backCamera = availableCameras.find(camera => 
            camera.label.toLowerCase().includes('back') || 
            camera.label.toLowerCase().includes('rear') ||
            camera.label.toLowerCase().includes('environment')
          );
          const preferredCamera = backCamera || availableCameras[0];
          if (preferredCamera) {
            setCurrentCameraId(preferredCamera.id);
          }
        }
      } catch (err) {
        console.error("Error initializing scanner:", err);
        if (mounted) {
          setError("Kamera erişim hatası");
        }
      }
    };

    initializeScanner();

    return () => {
      mounted = false;
      stopScanning();
    };
  }, []);

  // Handle page visibility change (iOS Safari optimization)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isScanning) {
        // Pause scanning when page becomes hidden
        stopScanning();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isScanning]);

  const startScanning = async () => {
    if (!videoRef.current) return;

    try {
      setIsScanning(true);
      setError("");

      // Create QR scanner instance
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => handleScanResult(result.data),
        {
          onDecodeError: (err) => {
            // Silently handle decode errors (normal when no QR code is visible)
          },
          preferredCamera: currentCameraId || 'environment',
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 5,
        }
      );

      // Set video constraints for mobile optimization
      const constraints = {
        facingMode: 'environment', // Use back camera by default
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      };

      await qrScannerRef.current.start();
      setHasPermission(true);
    } catch (err: any) {
      console.error("Error starting scanner:", err);
      setIsScanning(false);
      if (err.name === 'NotAllowedError') {
        setHasPermission(false);
        setError("Kamera erişim izni gerekli");
      } else if (err.name === 'NotFoundError') {
        setError("Kamera bulunamadı");
      } else {
        setError("Kamera başlatılamadı");
      }
    }
  };

  const stopScanning = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setIsScanning(false);
  };

  const handleScanResult = async (data: string) => {
    stopScanning();
    if (!userProfile) {
      toast({
        title: "Hata",
        description: "Kullanıcı bilgileri yüklenmedi",
        variant: "destructive"
      });
      return;
    }
    
    console.log('📱 QR Scanner - Scanned data:', data);
    console.log('📱 QR Scanner - User profile company_id:', userProfile.company_id);
    
    toast({
      title: "QR Kod Başarıyla Tarandı!",
      description: "Ürün bilgileri yükleniyor...",
    });
    
    // Use the universal QR resolver
    const result = await resolveQrForCompany(data, userProfile.company_id);
    console.log('📱 QR Scanner - Resolution result:', result);

    // Log the scan activity for analytics
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await logQrScanActivity(data, user.id, userProfile.company_id, result);
    }

    // Show appropriate toast and navigation
    if (result.success && result.productId) {
      toast({
        title: "Ürün Bulundu!",
        description: result.message,
      });
      setTimeout(() => {
        // Navigate to product detail page
        navigate(`/products/${result.productId}?qr_scan=true`);
      }, 1500);
    } else {
      toast({
        title: result.type === 'not_in_company' ? "Ürün Mevcut Değil" : 
               result.type === 'not_found' ? "Ürün Bulunamadı" : "QR Kod Hatası",
        description: result.message,
        variant: "destructive",
        duration: result.type === 'not_in_company' ? 8000 : 5000 // Longer duration for detailed messages
      });
      // For products not in company, give more time to read the detailed message
      const delay = result.type === 'not_in_company' ? 4000 : 2000;
      setTimeout(() => navigate('/dashboard'), delay);
    }
  };

  const toggleFlash = async () => {
    if (qrScannerRef.current) {
      try {
        if (flashEnabled) {
          await qrScannerRef.current.turnFlashOff();
        } else {
          await qrScannerRef.current.turnFlashOn();
        }
        setFlashEnabled(!flashEnabled);
      } catch (err) {
        console.error("Flash toggle error:", err);
        toast({
          title: "Flaş Hatası",
          description: "Flaş kontrolü desteklenmiyor",
          variant: "destructive"
        });
      }
    }
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;

    const currentIndex = cameras.findIndex(camera => camera.id === currentCameraId);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];

    setCurrentCameraId(nextCamera.id);

    if (qrScannerRef.current) {
      try {
        await qrScannerRef.current.setCamera(nextCamera.id);
      } catch (err) {
        console.error("Camera switch error:", err);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-panel-strong border-b border-white/10 sticky top-0 z-50 safe-top">
        <div className="flex items-center gap-4 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-xl font-semibold text-foreground">QR Kod Tara</h1>
        </div>
      </header>

      {/* Scanner Content */}
      <main className="p-6 space-y-8 safe-bottom">
        {/* Camera Permission Alert */}
        {hasPermission === false && (
          <div className="glass-panel p-6 border-destructive/20">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                <X className="text-destructive" size={32} />
              </div>
              <div>
                <h3 className="text-h3 font-semibold text-destructive mb-2">Kamera İzni Gerekli</h3>
                <p className="text-caption text-muted-foreground">
                  QR kod tarayabilmek için kamera erişim izni vermeniz gerekiyor.
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                Tekrar Dene
              </Button>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="glass-panel p-4 border-destructive/20">
            <div className="text-center">
              <p className="text-destructive text-caption">{error}</p>
            </div>
          </div>
        )}

        {/* Scanner Area */}
        <div className="glass-panel-strong p-8 text-center space-y-6 hover-lift">
          <div className="relative mx-auto w-80 h-80 border-2 border-accent/30 rounded-3xl overflow-hidden bg-black/20">
            {/* Video Element for Camera Feed */}
            <video
              ref={videoRef}
              className={`w-full h-full object-cover rounded-3xl ${isScanning ? 'block' : 'hidden'}`}
              playsInline
              muted
              autoPlay
              webkit-playsinline="true"
              style={{
                transform: currentCameraId.includes('front') || currentCameraId.includes('user') ? 'scaleX(-1)' : 'scaleX(1)',
                WebkitTransform: currentCameraId.includes('front') || currentCameraId.includes('user') ? 'scaleX(-1)' : 'scaleX(1)'
              }}
            />

            {/* Camera Icon When Not Scanning */}
            {!isScanning && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-accent/10 flex items-center justify-center">
                    <Camera className="text-accent" size={40} />
                  </div>
                </div>
              </div>
            )}

            {/* Scanning Overlay - Fixed Alignment */}
            {isScanning && (
              <div className="absolute inset-4 pointer-events-none">
                {/* Scanning Frame */}
                <div className="relative w-full h-full border-2 border-accent rounded-2xl">
                  {/* Corner Markers - Properly Centered */}
                  <div className="absolute -top-1 -left-1 w-8 h-8 border-l-4 border-t-4 border-accent rounded-tl-lg"></div>
                  <div className="absolute -top-1 -right-1 w-8 h-8 border-r-4 border-t-4 border-accent rounded-tr-lg"></div>
                  <div className="absolute -bottom-1 -left-1 w-8 h-8 border-l-4 border-b-4 border-accent rounded-bl-lg"></div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 border-r-4 border-b-4 border-accent rounded-br-lg"></div>
                  
                  {/* Scanning Animation Line */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-accent animate-pulse rounded-full"></div>
                  
                  {/* Center Crosshair */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-6 h-6 border-2 border-accent rounded-full animate-ping"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h2 className="text-h2 font-semibold text-foreground">
              {isScanning ? "Taranıyor..." : "QR Kodu Kameraya Yaklaştırın"}
            </h2>
            <p className="text-caption text-muted-foreground max-w-md mx-auto">
              {isScanning 
                ? "QR kod okunuyor, lütfen bekleyin..." 
                : "Ürün bilgilerini görüntülemek için kamerayı başlatın"
              }
            </p>
          </div>
        </div>

        {/* Scanner Controls */}
        <div className="grid grid-cols-3 gap-4">
          <div className="glass-panel p-4 hover-lift">
            <Button
              variant="ghost"
              size="lg"
              onClick={toggleFlash}
              disabled={!isScanning}
              className="w-full flex-col gap-3 h-20 no-zoom"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                flashEnabled ? 'bg-accent/20' : 'bg-muted/20'
              }`}>
                <Flashlight className={flashEnabled ? "text-accent" : "text-muted-foreground"} size={20} />
              </div>
              <span className="text-xs font-medium">{flashEnabled ? "Flaş Kapat" : "Flaş Aç"}</span>
            </Button>
          </div>

          <div className="glass-panel p-4 hover-lift">
            <Button
              variant="ghost"
              size="lg"
              onClick={switchCamera}
              disabled={!isScanning || cameras.length <= 1}
              className="w-full flex-col gap-3 h-20 no-zoom"
            >
              <div className="w-10 h-10 rounded-xl bg-muted/20 flex items-center justify-center">
                <SwitchCamera className="text-muted-foreground" size={20} />
              </div>
              <span className="text-xs font-medium">Kamera Değiştir</span>
            </Button>
          </div>

          <div className="glass-panel p-4 hover-lift">
            <Button
              variant="ghost"
              size="lg"
              onClick={() => {
                stopScanning();
                setError("");
              }}
              className="w-full flex-col gap-3 h-20 no-zoom"
            >
              <div className="w-10 h-10 rounded-xl bg-muted/20 flex items-center justify-center">
                <RotateCcw className="text-muted-foreground" size={20} />
              </div>
              <span className="text-xs font-medium">Sıfırla</span>
            </Button>
          </div>
        </div>

        {/* Start/Stop Scan Button */}
        <div className="glass-panel-strong p-6">
          <Button
            variant={isScanning ? "destructive" : "default"}
            size="lg"
            onClick={isScanning ? stopScanning : startScanning}
            disabled={hasPermission === false || !!error || loadingProfile || !userProfile}
            className={`w-full h-16 text-lg font-semibold no-zoom transition-all ${
              !isScanning ? 'bg-accent hover:bg-accent/90 text-white accent-glow' : ''
            }`}
          >
            {loadingProfile ? (
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                Kullanıcı Bilgileri Yükleniyor...
              </div>
            ) : isScanning ? (
              <div className="flex items-center gap-3">
                <X size={24} />
                Taramayı Durdur
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Camera size={24} />
                Kamerayı Başlat
              </div>
            )}
          </Button>
        </div>

        {/* Instructions */}
        <div className="glass-panel p-6">
          <div className="text-center space-y-4">
            <div className="w-12 h-12 mx-auto rounded-xl bg-accent/10 flex items-center justify-center">
              <Camera className="text-accent" size={24} />
            </div>
            <div>
              <h3 className="text-h3 font-semibold text-foreground mb-3">Evrensel QR Sistem</h3>
              <div className="space-y-2 text-caption text-muted-foreground max-w-md mx-auto">
                <p>• Tüm QR kodlar evrensel olarak çalışır</p>
                <p>• Sadece şirketinizin ürünleri gösterilir</p>
                <p>• QR kodu kamera görüş alanına getirin</p>
                <p>• Sistem otomatik olarak ürününüzü bulacak</p>
                <p>• Yetersiz ışıkta flaşı kullanabilirsiniz</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QRScanner;